/**
 * Подсчёт частей SMS (пакет 6, п.18) — зеркало Go (go/retention/sms_parts.go).
 * Оценка в редакторе, предпросмотр кампании и списание считают по одному
 * правилу; общий набор строк лежит в go/retention/testdata/sms_parts_fixture.json
 * и проверяется обоими тестами (smsParts.test.ts и sms_parts_test.go).
 *
 * Правила:
 * - весь текст в алфавите GSM-7 (латиница, цифры, обычная пунктуация): одна SMS
 *   до 160 единиц, дальше части по 153; символы ^ { } \ [ ~ ] | € и перевод
 *   страницы занимают по две единицы;
 * - любой символ вне GSM-7 (кириллица, «ёлочки», тире «—», эмодзи) переводит всё
 *   сообщение в UCS-2: одна SMS до 70 единиц UTF-16, дальше части по 67.
 *   Эмодзи вне BMP — две единицы.
 * Число частей — деление с округлением вверх; точное число потом сообщает SMSC.
 */

export const SMS_GSM_BASIC =
  "@£$¥èéùìòÇ\nØø\rÅå" +
  "Δ_ΦΓΛΩΠΨΣΘΞ" +
  "ÆæßÉ" +
  " !\"#¤%&'()*+,-./" +
  "0123456789:;<=>?" +
  "¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§" +
  "¿abcdefghijklmnopqrstuvwxyzäöñüà";
export const SMS_GSM_EXT = "\f^{}\\[~]|€";

const GSM_WIDTH = new Map<string, number>();
for (const ch of SMS_GSM_BASIC) GSM_WIDTH.set(ch, 1);
for (const ch of SMS_GSM_EXT) GSM_WIDTH.set(ch, 2);

export type SmsEncoding = "gsm7" | "ucs2";

export interface SmsSegments {
  encoding: SmsEncoding;
  /** Длина в единицах кодировки: септеты GSM-7 или слова UTF-16. */
  units: number;
  parts: number;
  /** Сколько единиц вмещает одна несоставная SMS (160 или 70). */
  single: number;
  /** Сколько единиц вмещает часть составного SMS (153 или 67). */
  perPart: number;
  /** Сколько единиц ещё можно дописать, не получив лишней части. */
  room: number;
}

/** Ширина каждого символа; gsm=false, если есть символ вне GSM-7. */
function unitWidths(chars: string[]): { widths: number[]; gsm: boolean } {
  const widths: number[] = [];
  let gsm = true;
  for (const ch of chars) {
    const w = GSM_WIDTH.get(ch);
    if (w === undefined) {
      gsm = false;
      break;
    }
    widths.push(w);
  }
  if (gsm) return { widths, gsm };
  return { widths: chars.map((ch) => ch.length), gsm: false };
}

export function smsAnalyze(text: string): SmsSegments {
  const { widths, gsm } = unitWidths(Array.from(text));
  const units = widths.reduce((sum, w) => sum + w, 0);
  const single = gsm ? 160 : 70;
  const perPart = gsm ? 153 : 67;
  const encoding: SmsEncoding = gsm ? "gsm7" : "ucs2";
  if (units <= single)
    return { encoding, units, parts: 1, single, perPart, room: single - units };
  const parts = Math.ceil(units / perPart);
  return { encoding, units, parts, single, perPart, room: parts * perPart - units };
}

/** Длина SMS в единицах кодировки (то же, что «Символов» в счётчике). */
export function smsLength(text: string): number {
  return smsAnalyze(text).units;
}

/** Сколько SMS-частей займёт готовый текст. */
export function smsParts(text: string): number {
  return smsAnalyze(text).parts;
}

/**
 * Сколько единиц убрать с конца текста, чтобы частей стало на одну меньше.
 * 0 — часть и так одна. Считаем по настоящему префиксу, поэтому если убрать
 * последний «непривычный» символ и текст вернётся в GSM-7, это учтено.
 */
export function smsShrink(text: string): number {
  const chars = Array.from(text);
  const base = smsAnalyze(text);
  if (base.parts <= 1) return 0;
  const { widths } = unitWidths(chars);
  let removed = 0;
  for (let i = chars.length - 1; i >= 0; i--) {
    removed += widths[i];
    if (smsAnalyze(chars.slice(0, i).join("")).parts < base.parts) return removed;
  }
  return removed;
}

/**
 * Символы вне GSM-7, из-за которых латинский текст уходит в Unicode (70 вместо
 * 160). Пусто, если в тексте есть кириллица (тогда Unicode и так неизбежен) или
 * нечего менять. Не больше `limit` разных символов.
 */
export function smsEncodingBlockers(text: string, limit = 6): string[] {
  if (/[\u0400-\u04FF]/.test(text)) return [];
  const found: string[] = [];
  for (const ch of text) {
    if (GSM_WIDTH.has(ch) || found.includes(ch)) continue;
    found.push(ch);
    if (found.length >= limit) break;
  }
  return found;
}

// ----- Короткие ссылки (п.19) -------------------------------------------
// Go заменяет каждую http(s)-ссылку SMS на короткую вида <база><код из 7 знаков>.
// Для счётчика подставляем «болванку» такой же длины: Latin-символы не меняют
// кодировку, длина совпадает с настоящей ссылкой.

/** Базовый адрес короткой ссылки по умолчанию (как в Go: slBase). */
export const SMS_SHORT_LINK_BASE = "https://sms.brasol.ru/api/s/";
export const SMS_SHORT_CODE_LENGTH = 7;
/** Длина готовой короткой ссылки по умолчанию. */
export const SMS_SHORT_LINK_LENGTH =
  SMS_SHORT_LINK_BASE.length + SMS_SHORT_CODE_LENGTH;
export const SMS_URL_MAX_LENGTH = 2000;

// Тот же шаблон, что slURLPattern в Go: только ASCII-символы адреса.
const URL_PATTERN = /https?:\/\/[A-Za-z0-9\-._~:/?#@!$&()*+,;=%[\]]+/gi;
const URL_TAIL = /[.,;:!?)\]]+$/;

/** Подходит ли адрес для замены: непустой хост и разумная длина. */
function shortenableUrl(url: string): boolean {
  return url.length <= SMS_URL_MAX_LENGTH && /^https?:\/\/[^/?#]*[A-Za-z0-9]/i.test(url);
}

/** Адреса http(s) в тексте SMS (без хвостовой пунктуации), по порядку. */
export function smsUrls(text: string): string[] {
  const urls: string[] = [];
  for (const match of text.matchAll(URL_PATTERN)) {
    const url = match[0].replace(URL_TAIL, "");
    if (shortenableUrl(url)) urls.push(url);
  }
  return urls;
}

export interface SmsShortLinkInfo {
  /** Текст для подсчёта: с «болванками» короткой ссылки, если замена выгодна. */
  text: string;
  /** Ссылки в тексте будут заменены короткими. */
  shortened: boolean;
  /** Сколько адресов в тексте можно сократить (даже если замена невыгодна). */
  links: number;
}

/**
 * Решение «заменять ли ссылки короткими» — зеркало Go (slCountedText): заменяем,
 * только если после замены частей не станет больше, иначе короткая ссылка
 * удорожила бы SMS и ссылка остаётся исходной (без подсчёта кликов).
 * `linkLength` — длина настоящей короткой ссылки (с сервера); 0 или меньше —
 * замена выключена, текст считается как есть.
 */
export function smsShortLinkInfo(
  text: string,
  linkLength: number = SMS_SHORT_LINK_LENGTH,
): SmsShortLinkInfo {
  if (linkLength <= 0) return { text, shortened: false, links: 0 };
  const stub = "x".repeat(linkLength);
  let links = 0;
  const stubbed = text.replace(URL_PATTERN, (match) => {
    const url = match.replace(URL_TAIL, "");
    if (!shortenableUrl(url)) return match;
    links++;
    return stub + match.slice(url.length);
  });
  if (!links) return { text, shortened: false, links: 0 };
  if (smsParts(stubbed) > smsParts(text)) return { text, shortened: false, links };
  return { text: stubbed, shortened: true, links };
}

/** Текст с подставленными «болванками» коротких ссылок — только для подсчёта длины. */
export function smsWithShortLinks(
  text: string,
  linkLength: number = SMS_SHORT_LINK_LENGTH,
): string {
  return smsShortLinkInfo(text, linkLength).text;
}
